import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Star, HelpCircle, Calendar, Car, Phone, Share2, Gift, Loader, CheckCircle, CreditCard, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import TripProgressTracker from "@/components/rider/TripProgressTracker";
import { API_URL } from "@/config/api";
import { toast } from "@/components/ui/use-toast";
import { getVenueName } from "@/utils/venueUtils";

const RiderMyTrips = () => {
  const navigate = useNavigate();
  const [showRewardsDialog, setShowRewardsDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [tripToCancel, setTripToCancel] = useState(null);
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [pastTrips, setPastTrips] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [walletLoading, setWalletLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  const pollingIntervalRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const initialTripCountRef = useRef(0);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.log('No auth token found, redirecting to login');
        toast({
          title: "Please log in",
          description: "You need to log in to view your trips",
          variant: "destructive",
        });
        navigate('/rider-login');
        return;
      }

      setAuthLoading(false);
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;

    const handleApiError = (response) => {
      if (response.status === 401 || response.status === 403) {
        console.log('API returned auth error, redirecting to login');
        toast({
          title: "Session expired",
          description: "Please log in again",
          variant: "destructive",
        });
        navigate('/rider-login');
        return true;
      }
      return false;
    };

    const fetchUpcomingTrips = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found for upcoming trips');
        setUpcomingLoading(false);
        return [];
      }

      try {
        const response = await fetch(`${API_URL}trips/?type=upcoming`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          }
        });

        if (handleApiError(response)) return [];

        if (response.ok) {
          const data = await response.json();
          console.log('Upcoming trips data:', data);
          const trips = data.results || data;
          setUpcomingTrips(trips);
          return trips;
        } else {
          console.error('Failed to fetch upcoming trips:', response.statusText);
          return [];
        }
      } catch (error) {
        console.error('Error fetching upcoming trips:', error);
        return [];
      } finally {
        setUpcomingLoading(false);
      }
    };

    const fetchPastTrips = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found for past trips');
        setPastLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}trips/?type=complete`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          }
        });

        if (handleApiError(response)) return;

        if (response.ok) {
          const data = await response.json();
          console.log('Past trips data:', data);
          setPastTrips(data.results || data);
        } else {
          console.error('Failed to fetch past trips:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching past trips:', error);
      } finally {
        setPastLoading(false);
      }
    };

    const startPolling = () => {
      setIsSyncing(true);
      
      pollingIntervalRef.current = setInterval(async () => {
        console.log('Polling for new trips...');
        const trips = await fetchUpcomingTrips();
        
        // Check if trip count changed
        if (trips.length !== initialTripCountRef.current) {
          console.log('Trip count changed, stopping sync');
          stopPolling();
        }
      }, 1000);

      // Stop polling after 15 seconds regardless
      syncTimeoutRef.current = setTimeout(() => {
        console.log('Sync timeout reached, stopping polling');
        stopPolling();
      }, 15000);
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      setIsSyncing(false);
    };

    // Initial data fetch
    const initializeData = async () => {
      const trips = await fetchUpcomingTrips();
      initialTripCountRef.current = trips.length;
      
      // Start polling if we're coming from a successful booking
      // (detected by empty initial trip count and recent page navigation)
      if (trips.length === 0) {
        startPolling();
      }
      
      fetchPastTrips();
    };

    initializeData();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [navigate, authLoading]);

  const handleDownloadWalletPass = async (tripId) => {
    if (!tripId) return;
    
    setWalletLoading(true);
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      toast({
        title: "Authentication Error",
        description: "Please log in again to download your wallet pass",
        variant: "destructive",
      });
      setWalletLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}trips/${tripId}/pkpass/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast({
            title: "Authentication Error",
            description: "Please log in again to download your wallet pass",
            variant: "destructive",
          });
          navigate('/rider-login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trip-${tripId}.pkpass`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Success",
        description: "Wallet pass downloaded successfully!",
      });
      
    } catch (error) {
      console.error('Error downloading wallet pass:', error);
      toast({
        title: "Download Error",
        description: "Failed to download wallet pass. Please try again.",
        variant: "destructive",
      });
    } finally {
      setWalletLoading(false);
    }
  };

  const handleCancelTrip = (trip) => {
    setTripToCancel(trip);
    setShowCancelDialog(true);
  };

  const confirmCancelTrip = async () => {
    if (!tripToCancel) return;
    
    setCancelLoading(true);
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      toast({
        title: "Authentication Error",
        description: "Please log in again to cancel your trip",
        variant: "destructive",
      });
      setCancelLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}trips/${tripToCancel.id}/cancel/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast({
            title: "Authentication Error",
            description: "Please log in again to cancel your trip",
            variant: "destructive",
          });
          navigate('/rider-login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove the cancelled trip from upcoming trips
      setUpcomingTrips(trips => trips.filter(trip => trip.id !== tripToCancel.id));
      
      toast({
        title: "Trip Cancelled",
        description: "Your trip has been successfully cancelled.",
      });
      
      setShowCancelDialog(false);
      setTripToCancel(null);
      
    } catch (error) {
      console.error('Error cancelling trip:', error);
      toast({
        title: "Cancellation Error",
        description: "Failed to cancel trip. Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setCancelLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Loader size={48} className="animate-spin text-gray-500 mb-4" />
        <p className="text-gray-600">Authenticating...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <main className="flex-grow">
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="bg-white px-4 py-6 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-black">My Trips</h1>
            <button 
              onClick={() => setShowRewardsDialog(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Rewards"
            >
              <Gift size={24} className="text-gray-600" />
            </button>
          </div>

          <div className="px-4 py-6 space-y-8">
            {/* Upcoming Trips Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-black">Upcoming Trips</h2>
                {isSyncing ? (
                  <div className="flex items-center gap-1">
                    <Loader size={16} className="animate-spin text-blue-500" />
                    <span className="text-sm text-blue-600">Syncing your booking...</span>
                  </div>
                ) : upcomingLoading ? (
                  <Loader size={20} className="animate-spin text-gray-500" />
                ) : (
                  <CheckCircle size={20} className="text-green-500" />
                )}
              </div>
              
              {upcomingTrips.length === 0 ? (
                <Card className="bg-white border border-gray-200">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-black mb-1">
                        You have no upcoming trips
                      </h3>
                      <Link 
                        to="/" 
                        className="text-gray-600 text-sm flex items-center gap-1 hover:text-black transition-colors"
                      >
                        Book A Party Bus <ArrowRight size={16} />
                      </Link>
                    </div>
                    <div className="text-right">
                      <Calendar size={32} className="text-red-500" />
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {upcomingTrips.map((trip) => {
                    const formatDate = (dateString) => {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                    };
                    
                    const formatTime = (dateString) => {
                      const date = new Date(dateString);
                      return date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      });
                    };

                    return (
                      <Card key={trip.id} className="bg-white border border-gray-200 overflow-hidden">
                        {/* Event Image - Always show for upcoming trips */}
                        <div className="h-32 bg-gray-100 relative">
                          <img 
                            src={trip.community_event?.image || "/Default_Party_Bus.jpeg"}
                            alt="Event image"
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Trip Details */}
                            <div>
                              <h3 className="text-lg font-medium text-black">
                                {getVenueName(trip.to_address)}
                              </h3>
                              <p className="text-gray-600 text-sm">
                                {formatDate(trip.pickup_at)} • {formatTime(trip.pickup_at)} • {trip.passenger_count} People
                              </p>
                              <p className="text-black font-medium">
                                ${trip.rider_price} {trip.rider_price_currency}
                              </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3">
                              <div className="flex gap-3">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => window.location.href = 'tel:(888) 539-7050'}
                                  className="flex items-center gap-2 border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-black"
                                >
                                  <Phone size={16} />
                                  Get Help
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleDownloadWalletPass(trip.id)}
                                  disabled={walletLoading}
                                  className="flex items-center gap-2 border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-black disabled:opacity-50"
                                >
                                  {walletLoading ? (
                                    <Loader size={16} className="animate-spin" />
                                  ) : (
                                    <CreditCard size={16} />
                                  )}
                                  View Ticket
                                </Button>
                              </div>
                              <div className="flex">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleCancelTrip(trip)}
                                  className="flex items-center gap-2 border-red-300 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700"
                                >
                                  <X size={16} />
                                  Cancel Trip
                                </Button>
                              </div>
                            </div>

                            {/* Progress Tracker */}
                            <TripProgressTracker currentStep={trip.claimed_by ? 2 : 1} />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Past Trips Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-black">Past Trips</h2>
                {pastLoading ? (
                  <Loader size={20} className="animate-spin text-gray-500" />
                ) : (
                  <CheckCircle size={20} className="text-green-500" />
                )}
              </div>

              <div className="space-y-4">
                {pastTrips.length === 0 ? (
                  <Card className="bg-white border border-gray-200">
                    <CardContent className="p-6 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-black mb-1">
                          You have no past trips
                        </h3>
                        <Link 
                          to="/" 
                          className="text-gray-600 text-sm flex items-center gap-1 hover:text-black transition-colors"
                        >
                          Book A Party Bus <ArrowRight size={16} />
                        </Link>
                      </div>
                      <div className="text-right">
                        <Car size={32} className="text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  pastTrips.map((trip) => {
                    const formatDate = (dateString) => {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      });
                    };
                    
                    const formatTime = (dateString) => {
                      const date = new Date(dateString);
                      return date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      });
                    };

                    return (
                      <Card key={trip.id} className="bg-white border border-gray-200 overflow-hidden">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Trip Details */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Car size={20} className="text-gray-600" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-medium text-black">
                                    {getVenueName(trip.to_address)}
                                  </h3>
                                  <p className="text-gray-600 text-sm">
                                    {formatDate(trip.pickup_at)} • {formatTime(trip.pickup_at)}
                                  </p>
                                  <p className="text-black font-medium">
                                    ${trip.rider_price} {trip.rider_price_currency}
                                  </p>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex flex-col gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="flex items-center gap-2 border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-black"
                                >
                                  <Star size={16} />
                                  Rate
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="flex items-center gap-2 border-gray-300 text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-black"
                                >
                                  <HelpCircle size={16} />
                                  Help
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </div>

            {/* Add padding bottom to account for fixed button */}
            <div className="pb-20"></div>
          </div>
        </div>
      </main>

      {/* Fixed Book New Party Bus Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <Link to="/">
          <Button className="w-full gradient-bg text-black hover:opacity-90 py-3 text-lg font-medium">
            Book New Party Bus
          </Button>
        </Link>
      </div>

      <footer className="bg-black text-white py-3 px-4 text-xs">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div>©2025 Disco. All Rights Reserved</div>
          <div className="flex gap-3">
            <a 
              href="https://www.discoveryconciergeservices.com/terms-conditions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Terms & Conditions
            </a>
            <span>|</span>
            <a 
              href="https://www.discoveryconciergeservices.com/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>

      {/* Rewards Dialog */}
      <Dialog open={showRewardsDialog} onOpenChange={setShowRewardsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Rewards</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <Gift size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium text-gray-600">Coming Soon</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Trip Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Cancel Trip</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <p className="text-lg text-gray-900 mb-2">
              Are you sure you want to cancel this trip?
            </p>
            {tripToCancel && (
              <p className="text-sm text-gray-600">
                {getVenueName(tripToCancel.to_address)} on{' '}
                {new Date(tripToCancel.pickup_at).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            )}
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelLoading}
              className="flex-1"
            >
              No, Keep Trip
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancelTrip}
              disabled={cancelLoading}
              className="flex-1"
            >
              {cancelLoading ? (
                <Loader size={16} className="animate-spin mr-2" />
              ) : null}
              Yes, Cancel Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default RiderMyTrips;
